const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

export const COMPARATOR_MIN_AGE = 0
export const COMPARATOR_MAX_AGE = 120

/** YYYY-MM-DD for today's local calendar date (for `<input type="date" max>`). */
export function localTodayIso(): string {
  const t = new Date()
  const y = t.getFullYear()
  const m = String(t.getMonth() + 1).padStart(2, '0')
  const d = String(t.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function calendarDate(isoDate: string): Date | null {
  if (!isoDate || !ISO_DATE.test(isoDate)) return null
  const match = isoDate.match(ISO_DATE)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2]) - 1
  const day = Number(match[3])
  const date = new Date(year, month, day)
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null
  return date
}

/**
 * Age in whole years from `isoDate` to `reference`, or null when either date
 * is missing or not a real calendar day.
 */
function wholeYearsBetween(isoDate: string, reference: Date): number | null {
  const dob = calendarDate(isoDate)
  if (!dob) return null
  let age = reference.getFullYear() - dob.getFullYear()
  if (
    reference.getMonth() < dob.getMonth() ||
    (reference.getMonth() === dob.getMonth() && reference.getDate() < dob.getDate())
  ) {
    age -= 1
  }
  return age
}

/**
 * Returns the age in whole years for a YYYY-MM-DD date string,
 * or null if the date is invalid or unparseable.
 */
export function ageFromIsoDate(isoDate: string): number | null {
  return wholeYearsBetween(isoDate, new Date())
}

/**
 * True when `dobIso` is a real date and the age on `referenceIso` is under 18.
 * A DOB exactly 18 years before the reference date is an adult.
 * An unparseable DOB or reference returns false; the caller decides how to
 * treat a missing date. The backend guard fail-closes separately.
 */
export function isUnder18On(dobIso: string, referenceIso: string): boolean {
  const reference = calendarDate(referenceIso)
  if (!reference) return false
  const age = wholeYearsBetween(dobIso, reference)
  return age !== null && age < 18
}

/**
 * Returns an error message if the comparator's DOB is missing, invalid, or
 * outside the allowed age range. Returns undefined when the value is valid.
 */
export function comparatorDobError(isoDate: string | undefined): string | undefined {
  if (!isoDate?.trim()) return 'Enter a date of birth'
  const age = ageFromIsoDate(isoDate)
  if (age === null) return 'Enter a valid date of birth'
  if (age < 0) return 'Date of birth cannot be in the future'
  if (age > COMPARATOR_MAX_AGE) {
    return `Enter a date of birth within the past ${COMPARATOR_MAX_AGE} years`
  }
  return undefined
}
